import createClient, { type Client, type Middleware } from 'openapi-fetch';
import { ApiError, networkError, toProblem, type Problem } from './problem';
import type { paths } from './schema';

export type HiveClient = Client<paths>;

export interface ClientOptions {
  /** Origem da API; vazio = mesma origem (o Vite encaminha /api e /provider-api para o núcleo). */
  baseUrl?: string;
  /** Token de acesso atual (renovado pelo módulo de autenticação). */
  getToken: () => Promise<string | null> | string | null;
  /** Tenant identificado pelo endereço; vai em todo pedido (X-Hive-Tenant). */
  tenant?: string | null;
  /** Idioma atual, para o Accept-Language. */
  getLang?: () => string;
  /** Chamado quando a API responde 401 (sessão vencida, token revogado, MFA exigido). */
  onUnauthorized?: (problem: Problem) => void;
  /** R01-17 B: ação sensível recusada por falta de MFA recente (403 step-up-required). */
  onStepUp?: (problem: Problem) => void;
  fetch?: typeof fetch;
}

/** Middleware que injeta autenticação, tenant e idioma, e avisa quando a sessão cai. */
export function hiveMiddleware(opts: ClientOptions): Middleware {
  return {
    async onRequest({ request }) {
      const token = await opts.getToken();
      if (token) request.headers.set('Authorization', `Bearer ${token}`);
      if (opts.tenant) request.headers.set('X-Hive-Tenant', opts.tenant);
      const lang = opts.getLang?.();
      if (lang) request.headers.set('Accept-Language', lang);
      return request;
    },
    async onResponse({ response }) {
      if (response.status === 401 && opts.onUnauthorized) {
        let body: unknown;
        try {
          body = await response.clone().json();
        } catch {
          body = null;
        }
        opts.onUnauthorized(toProblem(401, body));
      }
      if (response.status === 403 && opts.onStepUp) {
        let body: unknown;
        try {
          body = await response.clone().json();
        } catch {
          body = null;
        }
        const p = toProblem(403, body);
        if (p.code === 'step-up-required') opts.onStepUp(p);
      }
      return response;
    },
  };
}

export function createHiveClient(opts: ClientOptions): HiveClient {
  const client = createClient<paths>({
    baseUrl: opts.baseUrl ?? '',
    fetch: opts.fetch ?? ((input: Request) => globalThis.fetch(input)),
  });
  client.use(hiveMiddleware(opts));
  return client;
}

type Result<D> = { data?: D; error?: unknown; response: Response };

/**
 * Desembrulha a resposta do openapi-fetch: devolve os dados ou lança ApiError com o problem+json.
 * Falhas de rede viram ApiError com código "network".
 */
export async function unwrap<D>(p: Promise<Result<D>>): Promise<D> {
  let r: Result<D>;
  try {
    r = await p;
  } catch (e) {
    if (e instanceof ApiError) throw e;
    throw networkError(e);
  }
  if (!r.response.ok) throw new ApiError(r.response.status, toProblem(r.response.status, r.error));
  return r.data as D;
}
